import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, isApiError } from '../lib/api'
import { useBidProfile } from '../lib/bid-profile'
import { ConfirmModal } from './ConfirmModal'
import {
  SharePointBrowser,
  SPB_ROW_PX,
  spbHeightForRows,
  type BrowserLocation,
} from './SharePointBrowser'

/**
 * The bid's SharePoint site, on the bid overview — a THIN row under the usage + roster pair.
 *
 * Collapsed it says where the bid's material lives and links straight there; the toggle on the
 * right expands it into the site browser in place, so looking something up does not mean leaving
 * the workspace. Until someone pins a location, the row is the invitation to do so.
 *
 * Every member sees the row: knowing where the team's material lives is not a privilege, and the
 * pointer grants nothing — the backend reads SharePoint ON BEHALF OF the member, so someone who
 * cannot open the folder there cannot open it here. Setting or detaching it is editor/admin, which
 * is what the server enforces; the client check only avoids offering buttons that would 403.
 */

/** The pane opens on this many rows — enough to read a bid folder without scrolling. */
const DEFAULT_ROWS = 15
/** Fewer than this is not a list any more; more than this is a page, not a pane. */
const MIN_ROWS = 4
const MAX_ROWS = 40

const DEFAULT_HEIGHT = spbHeightForRows(DEFAULT_ROWS)
const MIN_HEIGHT = spbHeightForRows(MIN_ROWS)
const MAX_HEIGHT = spbHeightForRows(MAX_ROWS)
/** One arrow key moves the edge by one row — the same unit the pane is measured in. */
const STEP = SPB_ROW_PX

const clampHeight = (px: number): number => Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.round(px)))

/**
 * There is no resume marker here any more, and its absence is the point.
 *
 * This card used to mirror `open`/`picking` into sessionStorage on every change, because opening
 * the picker or the pane asked for a Graph token in the browser and that could answer with a
 * full-page redirect to Entra — which threw away every bit of React state on its way out. The
 * member clicked "Set the bid site", watched the browser leave and come back, and landed on a
 * dashboard that had forgotten the request. The marker made one click one action again.
 *
 * The browser holds no Graph token now (lib/graphSites.ts browses through the backend), so nothing
 * on this surface can navigate the page away. State that only existed to survive that journey has
 * nothing left to survive.
 */

export function BidSiteCard({ bid }: { bid: string }) {
  const { t } = useLang()
  const qc = useQueryClient()
  const { canEdit } = useBidProfile(bid)

  const [open, setOpen] = useState(false)
  const [picking, setPicking] = useState(false)
  const [detaching, setDetaching] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  /**
   * The pane's height in px — FIXED, and owned here rather than by its content. Browsing into a
   * folder of two files or of two hundred must not move the rest of the dashboard, so the browser
   * scrolls inside this box instead of growing it. The member sets the box, nothing else does.
   */
  const [height, setHeight] = useState(DEFAULT_HEIGHT)
  const drag = useRef<{ startY: number; startHeight: number } | null>(null)

  const query = useQuery({
    queryKey: ['bid-site', bid],
    queryFn: () => api.bidSite(bid),
    enabled: Boolean(bid),
  })
  const site = query.data?.site ?? null

  // The page opens the pane BY DEFAULT once a site is already pinned — the member came here to
  // work with what is already set, not to be told again that a folder exists. Keyed to the bid
  // (not fired once and forgotten) so switching bids without unmounting re-defaults for the new
  // one, but a member who then collapses it manually is not fought back open by this same effect
  // re-running on an unrelated re-render.
  const autoOpenedFor = useRef<string | null>(null)
  useEffect(() => {
    if (autoOpenedFor.current !== bid && site) {
      autoOpenedFor.current = bid
      setOpen(true)
    }
  }, [bid, site])

  /** A refusal is a RULE, so it is stated on the row; anything else is reported as itself. */
  function report(e: unknown): void {
    if (isApiError(e) && e.serverMessage) setError(e.serverMessage)
    else setError(e instanceof Error ? e.message : String(e))
  }

  async function pin(location: BrowserLocation): Promise<void> {
    setSaving(true)
    setError('')
    try {
      await api.setBidSite(bid, {
        siteId: location.site.id,
        siteName: location.site.displayName,
        siteUrl: location.site.webUrl,
        // Absent together on a SITE-only pin — the pane then opens on the site's libraries.
        ...(location.driveId && location.itemId
          ? { driveId: location.driveId, driveName: location.driveName ?? '', itemId: location.itemId }
          : {}),
        folderPath: location.folderPath,
        webUrl: location.webUrl,
      })
      await qc.invalidateQueries({ queryKey: ['bid-site', bid] })
      setPicking(false)
      // Land on the result: the member just chose this folder, so show them what is in it.
      setOpen(true)
    } catch (e) {
      // Stated in the dialog rather than thrown at the global error overlay (viewer, or a payload
      // SharePoint gave us that the domain will not store).
      report(e)
    } finally {
      setSaving(false)
    }
  }

  /** Forget the location. Nothing in SharePoint is touched — this drops a pointer. */
  async function detach(): Promise<void> {
    setSaving(true)
    setError('')
    try {
      await api.clearBidSite(bid)
      await qc.invalidateQueries({ queryKey: ['bid-site', bid] })
      setDetaching(false)
      // There is nothing left to browse, so do not leave an empty pane hanging open.
      setOpen(false)
    } catch (e) {
      setDetaching(false)
      report(e)
    } finally {
      setSaving(false)
    }
  }

  /** Drag the bottom edge. Tracked on the window so the pointer may leave the 6px grip. */
  function startResize(e: React.PointerEvent<HTMLDivElement>): void {
    e.preventDefault()
    drag.current = { startY: e.clientY, startHeight: height }
    const move = (ev: PointerEvent): void => {
      const from = drag.current
      if (from) setHeight(clampHeight(from.startHeight + ev.clientY - from.startY))
    }
    const stop = (): void => {
      drag.current = null
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', stop)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', stop)
  }

  /** The same resize from the keyboard — a drag handle nobody can reach is not a control. */
  function resizeByKey(e: React.KeyboardEvent<HTMLDivElement>): void {
    const by = e.key === 'ArrowDown' ? STEP : e.key === 'ArrowUp' ? -STEP : 0
    if (by) {
      e.preventDefault()
      setHeight((h) => clampHeight(h + by))
      return
    }
    if (e.key === 'Home') {
      e.preventDefault()
      setHeight(DEFAULT_HEIGHT)
    }
  }

  return (
    <section className="bidsite" aria-labelledby={`bidsite-h-${bid}`}>
      <div className="bidsite-row">
        <h2 id={`bidsite-h-${bid}`} className="bidsite-label">
          {t('bidsite_title')}
        </h2>

        {query.isLoading && <span className="bidsite-note">{t('loading')}</span>}

        {!query.isLoading && site && (
          <>
            {/* The SITE NAME alone — the one word that identifies whose material this is. The
                library and the folder path used to be spelled out here too, and the pane's
                breadcrumb right below said them a second time; the link still TARGETS the pinned
                folder (site.webUrl), so nothing is lost by not reciting the way down to it. The
                full URL stays in the tooltip for whoever wants it. */}
            <a
              className="bidsite-link"
              href={site.webUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={site.webUrl}
            >
              <span className="bidsite-part">{site.siteName || site.siteId}</span>
              <span className="bidsite-ext" aria-hidden="true">
                ↗
              </span>
              <span className="visually-hidden">{t('bidsite_opens_new_tab')}</span>
            </a>

            <span className="bidsite-actions">
              {canEdit && (
                <>
                  <button
                    type="button"
                    className="bidsite-change"
                    onClick={() => {
                      setError('')
                      setPicking(true)
                    }}
                  >
                    {t('bidsite_change')}
                  </button>
                  <button
                    type="button"
                    className="bidsite-unlink"
                    aria-label={t('bidsite_unlink')}
                    title={t('bidsite_unlink')}
                    onClick={() => {
                      setError('')
                      setDetaching(true)
                    }}
                  >
                    <IconUnlink />
                  </button>
                </>
              )}
              <button
                type="button"
                className="bidsite-toggle"
                aria-expanded={open}
                aria-controls={`bidsite-panel-${bid}`}
                aria-label={open ? t('bidsite_collapse') : t('bidsite_expand')}
                title={open ? t('bidsite_collapse') : t('bidsite_expand')}
                onClick={() => setOpen((v) => !v)}
              >
                <span aria-hidden="true">{open ? '▴' : '▾'}</span>
              </button>
            </span>
          </>
        )}

        {!query.isLoading && !site && (
          <>
            <span className="bidsite-note">{t('bidsite_none')}</span>
            {canEdit && (
              <span className="bidsite-actions">
                <button
                  type="button"
                  className="bidsite-set"
                  onClick={() => {
                    setError('')
                    setPicking(true)
                  }}
                >
                  {t('bidsite_set')}
                </button>
              </span>
            )}
          </>
        )}
      </div>

      {/* Kept mounted only while open: the pane lists the folder on mount, and a collapsed row must
          not have the backend read SharePoint for files nobody asked to see. The height is the
          member's, in px — the content never gets a say in it. */}
      {site && open && (
        <div className="bidsite-panel" id={`bidsite-panel-${bid}`}>
          <div className="bidsite-pane" style={{ height: `${height}px` }}>
            <SharePointBrowser
              bid={bid}
              mode="browse"
              start={site}
              // The pinned location IS the top of this pane — a folder, a library, or (site-only
              // pin) the site's libraries: nothing above it, and an "up one level" row inside it
              // that stops there. The row above already names the site.
              rootAnchored
              canIndex={canEdit}
              // A click-to-index job just finished — the Knowledge tab's list of indexed sources
              // may now be stale.
              onIndexed={() => void qc.invalidateQueries({ queryKey: ['sources', bid] })}
            />
          </div>
          <div
            className="bidsite-grip"
            role="separator"
            aria-orientation="horizontal"
            aria-label={t('bidsite_resize')}
            aria-valuenow={height}
            aria-valuemin={MIN_HEIGHT}
            aria-valuemax={MAX_HEIGHT}
            tabIndex={0}
            onPointerDown={startResize}
            onKeyDown={resizeByKey}
          />
        </div>
      )}

      {error && !picking && (
        <p className="bidsite-error" role="alert">
          {error}
        </p>
      )}

      {picking && (
        <BidSitePicker
          bid={bid}
          busy={saving}
          error={error}
          onSelect={(location) => void pin(location)}
          onClose={() => {
            setPicking(false)
            setError('')
          }}
        />
      )}

      {detaching && (
        <ConfirmModal
          title={t('bidsite_unlink_title')}
          message={t('bidsite_unlink_msg')}
          confirmLabel={t('bidsite_unlink_confirm')}
          danger
          onConfirm={() => void detach()}
          onClose={() => setDetaching(false)}
        />
      )}
    </section>
  )
}

/** A broken chain — "this bid and that folder are no longer attached". Decorative. */
function IconUnlink() {
  return (
    <svg className="nav-ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M9.5 14.5 8 16a3.5 3.5 0 0 1-5-5l1.5-1.5" />
      <path d="M14.5 9.5 16 8a3.5 3.5 0 0 1 5 5l-1.5 1.5" />
      <line x1="4" y1="4" x2="20" y2="20" />
    </svg>
  )
}

/** The picker dialog — the browser in `select` mode, in the app's standard modal shell. */
function BidSitePicker({
  bid,
  busy,
  error,
  onSelect,
  onClose,
}: {
  /** Whose SharePoint is being searched — the browse calls are authorised by bid membership. */
  bid: string
  busy: boolean
  error: string
  onSelect: (location: BrowserLocation) => void
  onClose: () => void
}) {
  const { t } = useLang()

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal bidsite-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('bidsite_pick_title')}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <div className="modal-head">
          <h2>{t('bidsite_pick_title')}</h2>
          <button className="modal-close" aria-label={t('close')} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body bidsite-modal-body">
          <SharePointBrowser bid={bid} mode="select" busy={busy} onSelect={onSelect} onCancel={onClose} />
          {error && (
            <p className="bidsite-error" role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
